<template>
  <div v-if="tags.length" class="table-tags-cell">
    <div class="table-tags-cell__visible">
      <BittsTag
        v-for="tag in displayedTags"
        :key="tag.id"
        :text="tag.label"
        size="x-small"
        variant="rounded"
      />
    </div>
    <BittsTooltip
      v-if="hiddenCount > 0"
      placement="bottomLeft"
      :mount-to-body="true"
      :mouse-enter-delay="0.3"
    >
      <span class="table-tags-cell__overflow"> +{{ hiddenCount }} </span>
      <template #title>
        {{ hiddenTags.map((tag) => tag.label).join(', ') }}
      </template>
    </BittsTooltip>
    <div v-if="hiddenCount > 0" class="table-tags-cell__gradient" />
  </div>
  <span v-else class="text-sm text-neutral-text-weak">&ndash;</span>
</template>

<script setup lang="ts">
import { BittsTag, BittsTooltip } from '@crossbeam/bitts';

import { ICellRendererParams } from '@ag-grid-community/core';
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

import type { TableCellTag } from './types';

const { params } = defineProps<{
  params: ICellRendererParams;
}>();

const TAG_PADDING = 24;
const CHAR_WIDTH = 6.3;
const GAP = 4;
const OVERFLOW_BADGE_WIDTH = 30;

const tags = computed<TableCellTag[]>(() => params.value ?? []);

const minWidth = params.column?.getColDef()?.minWidth;
const DEFAULT_COLUMN_WIDTH = minWidth ?? 200;
const columnWidth = ref(DEFAULT_COLUMN_WIDTH);

function getTagWidth(tag: TableCellTag) {
  return tag.label.length * CHAR_WIDTH + TAG_PADDING;
}

function fitTags(items: TableCellTag[], availableWidth: number) {
  let currentWidth = 0;
  const visible: TableCellTag[] = [];

  for (const tag of items) {
    const width = getTagWidth(tag) + (visible.length ? GAP : 0);
    if (currentWidth + width > availableWidth && visible.length) break;
    currentWidth += width;
    visible.push(tag);
  }

  return visible;
}

const displayedTags = computed(() => {
  const allFit = fitTags(tags.value, columnWidth.value);
  if (allFit.length === tags.value.length) return allFit;
  return fitTags(tags.value, columnWidth.value - OVERFLOW_BADGE_WIDTH);
});

const hiddenTags = computed(() => tags.value.slice(displayedTags.value.length));
const hiddenCount = computed(() => hiddenTags.value.length);

function onColumnResized() {
  columnWidth.value = params.column?.getActualWidth() ?? DEFAULT_COLUMN_WIDTH;
}

onMounted(async () => {
  await nextTick();
  columnWidth.value = params.column?.getActualWidth() ?? DEFAULT_COLUMN_WIDTH;
  params.api.addEventListener('columnResized', onColumnResized);
});

onBeforeUnmount(() => {
  if (params.api.isDestroyed()) return;
  params.api.removeEventListener('columnResized', onColumnResized);
});
</script>

<style lang="pcss">
@reference "../../../css/theme.css";
.table-tags-cell {
  @apply relative flex items-center gap-4 h-full w-full;
}
.table-tags-cell__visible {
  @apply flex items-center gap-4 overflow-hidden;
}
.table-tags-cell__overflow {
  @apply text-sm text-neutral-text shrink-0 cursor-default relative z-20;
}
.table-tags-cell__gradient {
  @apply absolute right-0 top-0 w-[40px] h-full z-10 pointer-events-none;
  background: linear-gradient(
    270deg,
    theme(colors.white) 40%,
    theme(colors.white / 0) 100%
  );
}
.ag-row-hover .table-tags-cell__gradient {
  background: linear-gradient(
    270deg,
    theme(colors.neutral-background-weak) 40%,
    theme(colors.neutral-background-weak / 0) 100%
  );
}
</style>
